import React from 'react';
import { Table, Button, Upload, Modal, Select, Input, Space, Tag, Popconfirm, Typography, notification } from 'antd';
import { UploadOutlined, DownloadOutlined, DeleteOutlined, PaperClipOutlined, EyeOutlined } from '@ant-design/icons';
import moment from 'moment';

import request from '../lib/request';

export const ATTACHMENT_CATEGORIES = [
  { value: 'GENERAL', label: 'General' },
  { value: 'KYC', label: 'KYC' },
  { value: 'SIGNED_COPY', label: 'Signed copy' },
  { value: 'CORRESPONDENCE', label: 'Correspondence' },
  { value: 'SUPPORTING_DOCUMENT', label: 'Supporting document' },
];

const CATEGORY_COLORS = { GENERAL: 'default', KYC: 'purple', SIGNED_COPY: 'green', CORRESPONDENCE: 'blue', SUPPORTING_DOCUMENT: 'gold' };
const PREVIEWABLE = /^(application\/pdf|image\/)/;

export function formatBytes(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** The API is token-authenticated, so downloads go through fetch and a blob URL rather than a plain link. */
function fetchBlob(attachment, inline) {
  return request.get(`/attachments/${attachment.id}/download${inline ? '?inline=1' : ''}`).then(async (r) => {
    if (!r.ok || (r.headers.get('content-type') || '').includes('application/json')) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.message || 'Download failed.');
    }
    return r.blob();
  });
}

export function downloadAttachment(attachment) {
  return fetchBlob(attachment, false)
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    })
    .catch((e) => notification.error({ message: e.message }));
}

export function previewAttachment(attachment) {
  return fetchBlob(attachment, true)
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    })
    .catch((e) => notification.error({ message: e.message }));
}

/** Per-owner file counts for list screens: { [ownerId]: count }. */
export function useAttachmentCounts(ownerType, ownerIds) {
  const [counts, setCounts] = React.useState({});
  const key = (ownerIds || []).join(',');
  const refresh = React.useCallback(() => {
    if (!key) {
      setCounts({});
      return;
    }
    request
      .get('/attachments/summary', { owner_type: ownerType, owner_ids: key })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setCounts(r.data);
      });
  }, [ownerType, key]);
  React.useEffect(refresh, [refresh]);
  return { counts, refresh };
}

/**
 * The files on one record: list, upload, preview/download, remove. Drop it on
 * any page that shows a record which carries files (KYC document, document
 * request, engagement letter, task, lead, client, invoice).
 */
export function AttachmentsPanel({ ownerType, ownerId, categories, canDelete = true, onChange }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [category, setCategory] = React.useState(categories ? categories[0] : undefined);
  const [description, setDescription] = React.useState('');

  const fetchRows = React.useCallback(() => {
    if (!ownerId) return;
    setLoading(true);
    request
      .get('/attachments', { owner_type: ownerType, owner_id: ownerId })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setRows(r.data);
        else notification.error({ message: r.message });
      })
      .finally(() => setLoading(false));
  }, [ownerType, ownerId]);

  React.useEffect(fetchRows, [fetchRows]);

  const changed = () => {
    fetchRows();
    if (onChange) onChange();
  };

  const customRequest = ({ file, onSuccess, onError }) => {
    const data = new FormData();
    data.append('file', file);
    data.append('owner_type', ownerType);
    data.append('owner_id', ownerId);
    if (category) data.append('category', category);
    if (description) data.append('description', description);
    setUploading(true);
    request
      .post('/attachments', data)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: `${file.name} uploaded` });
          setDescription('');
          onSuccess(r.data);
          changed();
        } else {
          notification.error({ message: r.message });
          onError(new Error(r.message));
        }
      })
      .catch((e) => {
        notification.error({ message: e.message });
        onError(e);
      })
      .finally(() => setUploading(false));
  };

  const handleDelete = (attachment) => {
    request
      .delete(`/attachments/${attachment.id}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: 'File removed' });
          changed();
        } else {
          notification.error({ message: r.message });
        }
      });
  };

  const columns = [
    {
      title: 'File',
      dataIndex: 'original_name',
      render: (v, a) => (
        <span>
          <PaperClipOutlined /> <Typography.Link onClick={() => (PREVIEWABLE.test(a.mime_type) ? previewAttachment(a) : downloadAttachment(a))}>{v}</Typography.Link>
          {a.description && (
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
              {a.description}
            </Typography.Text>
          )}
        </span>
      ),
    },
    { title: 'Category', dataIndex: 'category', width: 150, render: (v) => <Tag color={CATEGORY_COLORS[v]}>{v.replace('_', ' ')}</Tag> },
    { title: 'Size', dataIndex: 'size_bytes', width: 90, render: formatBytes },
    { title: 'Uploaded', key: 'uploaded', width: 200, render: (_, a) => `${moment(a.createdAt).format('YYYY-MM-DD HH:mm')}${a.UploadedBy ? ` - ${a.UploadedBy.name}` : ''}` },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, a) => (
        <Space size={4}>
          {PREVIEWABLE.test(a.mime_type) && <Button size="small" type="text" icon={<EyeOutlined />} title="Open" onClick={() => previewAttachment(a)} />}
          <Button size="small" type="text" icon={<DownloadOutlined />} title="Download" onClick={() => downloadAttachment(a)} />
          {canDelete && (
            <Popconfirm title="Remove this file?" onConfirm={() => handleDelete(a)}>
              <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Remove" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        {categories && <Select style={{ width: 190 }} value={category} onChange={setCategory} options={ATTACHMENT_CATEGORIES.filter((c) => categories.includes(c.value))} />}
        <Input placeholder="Description (optional)" style={{ width: 260 }} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={255} />
        <Upload customRequest={customRequest} showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.tif,.tiff,.bmp,.heic,.doc,.docx,.xls,.xlsx,.xlsm,.csv,.txt,.rtf,.odt,.ods,.ppt,.pptx,.msg,.eml,.xml,.json">
          <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
            Upload file
          </Button>
        </Upload>
        <Typography.Text type="secondary">PDF, images or office documents, up to 25 MB.</Typography.Text>
      </Space>
      <Table rowKey="id" size="small" columns={columns} dataSource={rows} loading={loading} pagination={false} locale={{ emptyText: 'No files yet' }} />
    </div>
  );
}

/** A small "Files (n)" button that opens the panel in a modal - for table rows. */
export function AttachmentsButton({ ownerType, ownerId, count, title, categories, canDelete, onChange, size = 'small' }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button size={size} icon={<PaperClipOutlined />} onClick={() => setOpen(true)}>
        Files{count ? ` (${count})` : ''}
      </Button>
      <Modal title={title || 'Files'} open={open} onCancel={() => setOpen(false)} footer={null} width={860} destroyOnClose>
        <AttachmentsPanel ownerType={ownerType} ownerId={ownerId} categories={categories} canDelete={canDelete} onChange={onChange} />
      </Modal>
    </>
  );
}

export default AttachmentsPanel;
